import { Component } from '@theme/component';

class TWForms extends Component {

  constructor() {
    super();
    this.submitEndpoint = this.dataset.endpoint;
    this.products = [];
  }

  connectedCallback() {
    this.form = this.querySelector('[data-form]');
    this.addProductButton = this.querySelector('[data-add-product]');
    this.productsList = this.querySelector('[data-products-list]');
    this.productTemplate = this.querySelector('[data-product-template]');
    this.totalProductsEl = this.querySelector('[data-total-products]');
    this.statusEl = this.querySelector('[data-status]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.productsForm = this.querySelector('[data-products-form]');

    if (!this.form) return;

    this.form.addEventListener('submit', this.handleSubmit);

    if (this.addProductButton) {
      this.addProductButton.addEventListener('click', this.handleAddProduct);
    }

    if (!this.submitEndpoint) {
      this.setStatus('Missing submission endpoint.', 'error');
    }
  }

  handleAddProduct = () => {
    if (!this.productsForm || !this.productTemplate || !this.productsList) return;

    const productInputs = this.productsForm.querySelectorAll('[data-form-input]');
    const productData = {};
    const productsObj = {};
    let hasValue = false;

    productInputs.forEach((input) => {
      const label = input.dataset.formInput || input.name;
      const name = input.name;
      const value = input.value?.trim();
      if (label) {
        productData[label] = value;
      }
      if (name) {
        productsObj[name] = value;
      }
      if (value) {
        hasValue = true;
      }
    });

    if (!hasValue) {
      this.setStatus('Add at least one product field before adding a product.', 'error');
      return;
    }

    const productId = `${Date.now()}`;
    this.products.push({ id: productId, ...productsObj });
    const productNode = this.productTemplate.content.cloneNode(true);
    const summaryEl = productNode.querySelector('[data-product-summary]');
    const removeBtn = productNode.querySelector('[data-remove-product]');
    if (summaryEl) {
      summaryEl.innerHTML = this.buildProductSummary(productData);
    }
    if (removeBtn) {
      removeBtn.dataset.productId = productId;
      removeBtn.addEventListener('click', this.handleRemoveProduct);
    }

    this.productsList.appendChild(productNode);
    this.updateTotalProducts();
    this.clearProductInputs(productInputs);
    this.setStatus('', 'info');
  };

  handleRemoveProduct = (event) => {
    const productId = event.currentTarget.dataset.productId;
    if (!productId) return;

    this.products = this.products.filter((product) => product.id !== productId);
    const productCard = event.currentTarget.closest('[data-product-card]') || event.currentTarget.parentElement;
    if (productCard) {
      productCard.remove();
    }
    this.updateTotalProducts();
  };

  buildProductSummary(productData) {
    const entries = Object.entries(productData).map(([key, value]) => `${key}: ${value}`);
    return entries.length ? entries.join('<br>') : 'Product added';
  }

  clearProductInputs(inputs) {
    inputs.forEach((input) => {
      input.value = '';
    });
  }

  updateTotalProducts() {
    const total = this.products.reduce((sum, product) => { 
      const quantity = parseInt(product['quantity'] || '1', 10);
      return sum + quantity;
    }, 0)
    if (this.totalProductsEl) {
      this.totalProductsEl.textContent = `${total}`;
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.submitEndpoint) {
      this.setStatus('Missing submission endpoint.', 'error');
      return;
    }


    this.setLoading(true);
    this.setStatus('', 'info');

    const formData = new FormData(this.form);

    if (this.productsForm) {
      const productInputs = Array.from(
        this.productsForm.querySelectorAll('[data-form-input]'),
      );
      productInputs.forEach((input) => {
        if (input.name) {
          formData.delete(input.name);
        }
      });
    }

    const payload = Object.fromEntries(formData.entries());
    payload.caseItems = this.products.map(({ id, ...rest }) => rest);

    try {
      const response = await fetch(this.submitEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      this.form.reset();
      this.products = [];
      if (this.productsList) {
        this.productsList.innerHTML = '';
      }
      this.updateTotalProducts();
      this.setStatus('Form submitted successfully.', 'success');
    } catch (error) {
      console.error(error);
      this.setStatus('Something went wrong. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  };


  setFieldValidity(input, isValid) {
    if (isValid) {
      input.classList.remove('border-red-500');
      input.classList.add('border-border-neutral');
      input.removeAttribute('aria-invalid');
    } else {
      input.classList.add('border-red-500');
      input.setAttribute('aria-invalid', 'true');
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  setStatus(message, type) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.classList.add('hidden');
      this.statusEl.textContent = '';
      return;
    }

    this.statusEl.textContent = message;
    this.statusEl.classList.remove('hidden');
    this.statusEl.classList.remove('text-green-600', 'text-red-600');
    if (type === 'success') {
      this.statusEl.classList.add('text-green-600');
    }
    if (type === 'error') {
      this.statusEl.classList.add('text-red-600');
    }
  }
}
if (!customElements.get('tw-forms')) {
  customElements.define('tw-forms', TWForms);
}
